import { AxiosRequestConfig } from "axios";
import { createContext, PropsWithChildren, useContext } from "react";
import { useAuth } from "./auth";
import { api } from "../api/api";

interface IAuthApiState {
  get<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  post<T, D = any>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig
  ): Promise<T>;
  put<T, D = any>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig
  ): Promise<T>;
  delete<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
}

const initialState: IAuthApiState = {
  get: () => Promise.resolve(null as any),
  post: () => Promise.resolve(null as any),
  put: () => Promise.resolve(null as any),
  delete: () => Promise.resolve(null as any),
};

export const AuthApiContext = createContext<IAuthApiState>(initialState);

export const useApi = () => useContext(AuthApiContext);

export const AuthApiProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { getToken } = useAuth();

  const doGet = async <T,>(url: string, config?: AxiosRequestConfig) => {
    const token = await getToken();
    return api.get<T>(url, {
      ...config,
      headers: {
        ...config?.headers,
        Authorization: token ? `Bearer ${token}` : undefined,
      },
    });
  };

  const doPost = async <T, D = any>(url: string, data?: D) => {
    const token = await getToken();
    return api.post<T, D>(url, data, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
    });
  };

  const doPut = async <T, D = any>(url: string, data?: D) => {
    const token = await getToken();
    return api.put<T, D>(url, data, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
    });
  };

  const doDelete = async <T,>(url: string) => {
    const token = await getToken();
    return api.delete<T>(url, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
    });
  };

  return (
    <AuthApiContext.Provider
      value={{
        get: doGet,
        post: doPost,
        put: doPut,
        delete: doDelete,
      }}
    >
      {children}
    </AuthApiContext.Provider>
  );
};
